import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { ErrorAlert } from "@/components/common/ErrorAlert";
import { ProgressPanel } from "@/components/results/ProgressPanel";
import type { ProgressState } from "@/types";

export interface ReviewProgressLayoutProps {
  title?: string;
  backTo?: string;
  progress?: ProgressState;
  error?: string | null;
  onErrorDismiss?: () => void;
  children?: React.ReactNode;
}

export function ReviewProgressLayout({
  title,
  backTo = "/contract-review",
  progress,
  error,
  onErrorDismiss,
  children,
}: ReviewProgressLayoutProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col bg-[#f4f5f9] dark:bg-slate-950">
      <header className="border-b border-[var(--lm-border-subtle)] bg-[var(--bar-bg)] px-6 py-4 backdrop-blur-sm dark:border-slate-800/50 dark:bg-slate-900/80">
        <div className="flex items-center gap-4">
          <IconButton asChild title="Back to contract reviews">
            <Link to={backTo}>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </IconButton>
          {title ? (
            <h1 className="text-lg font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
              {title}
            </h1>
          ) : null}
        </div>
      </header>
      <div className="flex min-h-0 flex-1 items-center justify-center p-6">
        <div className="w-full max-w-2xl space-y-4">
          {error ? (
            <>
              <ErrorAlert message={error} className="mt-0" />
              {onErrorDismiss ? (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={onErrorDismiss}
                    className="text-sm font-medium text-violet-600 hover:text-violet-500 dark:text-violet-400 dark:hover:text-violet-300"
                  >
                    Dismiss
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
          {children ?? (progress ? <ProgressPanel progress={progress} /> : null)}
        </div>
      </div>
    </div>
  );
}
